// @givmo/compliance — the words and the pure rules of a request someone was asked to sign
// (CCM-R1-SIGN-WEB, the signer's side; slice 2 §6(b), (c), (g), frames 38–40, and slice 3's frame
// 82; D-CCM-22, D-CCM-27). Signing is always the person pressing Sign in Givmo: no AI signs, so
// slice 2's approve-for-AI page (frame 47) and its prompt row are not built (D-CCM-31).
//
// ══ WHERE EVERY LINE COMES FROM ════════════════════════════════════════════════════════════════
//
// APPROVED: slice 2's frames and its words-only block (`NEW-STRINGS-FOR-APPROVAL.md`, item 30,
// D-CCM-22), slice 3's frame 82 (D-CCM-27), the lines Jonathan approved on this cut's renders
// (D-CCM-29, 2026-10-03: S2, S3, S5, S10, S11 and Q7's "will be told"), and the passkey lines W1 and
// W3 as written (D-CCM-36, 2026-10-06, on cut 1006-4's renders). SERVED: the factual line, the
// statement a signature affirms and every refusal's `detail` — rendered as served, never re-worded
// here. BUILT: the package's own load and not-found lines, and `@givmo/step-up`'s fallback line for
// a prompt whose level isn't known here. No line here carries a root the suite's terminology rail
// bans: the form's own statement is pointed to ("the statement printed above the signature line"),
// never quoted.

import { stepUpWords, type StepUpContext, type StepUpCopyTable } from '@givmo/step-up';
import { localDayOf, displayComplianceDate } from './compliance-decode';
import { longComplianceDate } from './compliance-panel-model';
import type { Rect } from './compliance-preparation-decode';
import type {
  SigningAct,
  SigningDocument,
  SigningLevel,
  SigningPage,
  SigningRequestDetail,
  SigningSlot,
} from './compliance-signing-decode';

type PersonName = ((userId: string) => string | undefined) | undefined;

// ── Dates, in the request's own zone ───────────────────────────────────────────────────────────

/**
 * "Nov 16, 2026": the day something happened, in the zone the request writes its dates in (the
 * manifest's, as the record and the notices do), else the viewer's.
 */
export function shortDateIn(iso: string | undefined, timeZone?: string): string {
  const day = localDayOf(iso, timeZone);
  return day === undefined ? '—' : displayComplianceDate(day);
}

/** "November 30, 2026", in the same zone. */
export function longDateIn(iso: string | undefined, timeZone?: string): string {
  const day = localDayOf(iso, timeZone);
  return day === undefined ? '—' : longComplianceDate(day);
}

/** "9:12 AM", in the same zone. */
export function timeIn(iso: string | undefined, timeZone?: string): string {
  if (iso === undefined) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone !== undefined ? { timeZone } : {}),
  });
}

/** The day and the time of an instant, in the filing's zone (the request's), else the viewer's. */
export interface When {
  day: string;
  time: string;
}

export function whenOf(iso: string | undefined, timeZone?: string): When {
  return { day: shortDateIn(iso, timeZone), time: timeIn(iso, timeZone) };
}

// ── Who is who ─────────────────────────────────────────────────────────────────────────────────

/** The notices' own words for a person the roster cannot name (D-CCM-23). */
export const SOMEONE_IN_YOUR_ORGANIZATION = 'Someone in your organization';
/**
 * The same words mid-sentence (after "by", "asked" or a middle dot, inside brackets, or after
 * another person's name): lowercase, so they never read as a name. The capital stays only where
 * they open a sentence or stand alone (D-CCM-99).
 */
export const SOMEONE_IN_YOUR_ORGANIZATION_MID_SENTENCE = 'someone in your organization';

/** The organization as a line names it: its name, else "Your organization". */
export function organizationOf(name: string | undefined): string {
  const trimmed = name?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : 'Your organization';
}

/** The person who sent this version out, by the roster. */
export function senderName(
  detail: SigningRequestDetail,
  personName: PersonName,
): string | undefined {
  const userId = detail.version.issuedByUserId ?? detail.request.issuedByUserId;
  return userId !== undefined ? personName?.(userId) : undefined;
}

/** A first name, for "{first name}'s note": the name's first word. */
export function firstNameOf(name: string | undefined): string | undefined {
  const first = name?.trim().split(/\s+/)[0];
  return first !== undefined && first.length > 0 ? first : undefined;
}

// ── The caller's own place ─────────────────────────────────────────────────────────────────────

/** The caller's places on the live version, in the form's order. */
export function myPlaces(detail: SigningRequestDetail): SigningSlot[] {
  const mine = new Set(detail.mySlots.slotIds);
  return detail.slots.filter((slot) => mine.has(slot.slotId));
}

/** The caller's first place: the page the signer page opens on and draws the typed name at. */
export function firstPlace(detail: SigningRequestDetail): SigningSlot | undefined {
  return myPlaces(detail)[0];
}

/** The document a place is on. */
export function documentOf(
  detail: SigningRequestDetail,
  slot: SigningSlot | undefined,
): SigningDocument | undefined {
  return slot !== undefined
    ? detail.documents.find((document) => document.documentId === slot.documentId)
    : detail.documents[0];
}

/** Whether every place the caller holds stands signed. */
export function iHaveSigned(detail: SigningRequestDetail): boolean {
  const places = myPlaces(detail);
  return places.length > 0 && places.every((slot) => slot.standing === 'signed');
}

/** The caller's newest act of a kind, by when it happened. */
export function myLatestAct(
  detail: SigningRequestDetail,
  kind: SigningAct['kind'],
): SigningAct | undefined {
  const mine = new Set(detail.mySlots.slotIds);
  return detail.acts
    .filter((act) => act.kind === kind && act.slotIds.some((slotId) => mine.has(slotId)))
    .sort((a, b) => (a.occurredAt ?? '').localeCompare(b.occurredAt ?? ''))
    .at(-1);
}

// ── Where a place lands on its page's image ────────────────────────────────────────────────────

/** A page's crop box in PDF points: served, else the image's own size over its scale. */
export function cropBoxOf(page: SigningPage): Rect | undefined {
  if (page.cropBox !== undefined) return page.cropBox;
  if (page.scale === undefined) return undefined;
  return [0, 0, page.width / page.scale, page.height / page.scale];
}

/**
 * The drawing geometry the composer uses for the typed name (backend
 * `app/domain/charity_compliance_signing_appearance.py`, `typed-name-v2`): navy ink, 20 pt where the
 * place allows and down to 8 pt, fitted to 94 % of the place's width inside a 1 pt margin, the
 * baseline resting 4.6 pt above the place's bottom at 20 pt and scaled with the size. The preview
 * is a preview: the signed form is drawn by the server.
 */
export const SIGNATURE_INK = '#14223d';
export const SIGNATURE_MAX_PT = 20;
export const SIGNATURE_MIN_PT = 8;
const SIGNATURE_MARGIN_PT = 1;
const SIGNATURE_WIDTH_SHARE = 0.94;
const SIGNATURE_REST_PT_AT_MAX = 4.6;

/** The private family the font is registered under, here and in the backend's composer. */
export const SIGNATURE_FONT_FAMILY = 'Givmo Signature Script';

export interface TypedNameLayout {
  /** The page's crop box size, in points: the overlay's view box. */
  viewWidth: number;
  viewHeight: number;
  /** The place, in view-box coordinates (from the top left). */
  place: { x: number; y: number; width: number; height: number };
  /** Where the text starts and its baseline, in view-box coordinates. */
  x: number;
  baseline: number;
  fontSize: number;
}

/**
 * Lay the typed name out at a place, given its width at 1 pt (`measure`, from the loaded font):
 * the largest size from 20 pt down to 8 pt whose width fits, clipped to the place below that. A
 * rotated page is not drawn on: no preview beats a wrong one.
 */
export function typedNameLayout(
  rect: Rect,
  page: SigningPage,
  widthAtOnePoint: number,
): TypedNameLayout | undefined {
  if (page.rotation % 360 !== 0) return undefined;
  const crop = cropBoxOf(page);
  if (crop === undefined) return undefined;
  const [cx0, cy0, cx1, cy1] = crop;
  const [x0, y0, x1, y1] = rect;
  const viewWidth = cx1 - cx0;
  const viewHeight = cy1 - cy0;
  if (viewWidth <= 0 || viewHeight <= 0) return undefined;
  const room = Math.max(0, (x1 - x0 - 2 * SIGNATURE_MARGIN_PT) * SIGNATURE_WIDTH_SHARE);
  const fitting = widthAtOnePoint > 0 ? room / widthAtOnePoint : SIGNATURE_MAX_PT;
  const fontSize = Math.max(SIGNATURE_MIN_PT, Math.min(SIGNATURE_MAX_PT, fitting));
  const rest = (SIGNATURE_REST_PT_AT_MAX * fontSize) / SIGNATURE_MAX_PT;
  return {
    viewWidth,
    viewHeight,
    place: { x: x0 - cx0, y: cy1 - y1, width: x1 - x0, height: y1 - y0 },
    x: x0 - cx0 + SIGNATURE_MARGIN_PT,
    baseline: cy1 - y0 - rest,
    fontSize,
  };
}

// ── The signer page's words ────────────────────────────────────────────────────────────────────

export const SIGNATURE_REQUESTED = 'Signature requested';
export const SHOW_ALL_PAGES = 'Show all pages';
export const DOWNLOAD_THIS_PDF = 'Download this PDF';
export const YOUR_SIGNATURE = 'Your signature';
export const TYPE_YOUR_FULL_NAME = 'Type your full name';
export const TITLE_LABEL = 'Title';
export const DATE_LABEL = 'Date';
export const SIGN = 'Sign';
export const DECLINE = 'Decline';
export const WITHDRAW_MY_SIGNATURE = 'Withdraw my signature';
export const SIGNED_FORM_READY = 'The signed form is ready.';
export const DOWNLOAD_THE_SIGNED_FORM = 'Download the signed form';
export const DECLINE_TITLE = 'Decline to sign?';
export const REASON_OPTIONAL = 'Reason (optional)';
export const KEEP_IT = 'Keep it';
/** Built (the obligation page's): a request this person cannot open, or that does not exist. */
export const NOT_FOUND_TITLE = 'Not found';
export const NOT_FOUND_LINE = 'We couldn’t open that entry.';
/** Built (the workbench's): a read that failed. */
export const LOAD_FAILED = 'We couldn’t load this filing. Please try again.';
/** Built (the workbench's): a link that could not be made. */
export const OPEN_FAILED = 'We couldn’t open that document. Please try again.';

/** "Maya Solberg asked you to sign on Nov 16, 2026 · Sign by November 30, 2026" (frame 38). */
export function askedLine(
  sender: string | undefined,
  issuedAt: string | undefined,
  signBy: string | undefined,
  timeZone?: string,
): string {
  const who = sender ?? SOMEONE_IN_YOUR_ORGANIZATION;
  const asked = `${who} asked you to sign on ${shortDateIn(issuedAt, timeZone)}`;
  return signBy !== undefined ? `${asked} · Sign by ${longDateIn(signBy, timeZone)}` : asked;
}

/** "Maya’s note: “…”" (frame 38), or the note alone for a sender the roster cannot name. */
export function noteLine(sender: string | undefined, message: string): string {
  const first = firstNameOf(sender);
  return first !== undefined ? `${first}’s note: “${message}”` : `“${message}”`;
}

/** "Page 4 of 4", and "…, as signed" once the form is (frames 38, 40). */
export function pageOfLine(page: number, count: number, asSigned = false): string {
  return asSigned ? `Page ${page} of ${count}, as signed` : `Page ${page} of ${count}`;
}

/** "Show page 4": back from every page to the one with the place (the workbench's pattern). */
export function showPageLine(page: number): string {
  return `Show page ${page}`;
}

/** "How it looks on page 4". */
export function howItLooksLine(page: number): string {
  return `How it looks on page ${page}`;
}

/** "President, as Riverbend Community Trust states it" (Title; stated, never checked). */
export function asStatedLine(capacity: string, organization: string | undefined): string {
  const who = organization?.trim() ? organization.trim() : 'your organization';
  return `${capacity}, as ${who} states it`;
}

/** "The day you sign (November 17, 2026)": today, in the request's zone. */
export function dayYouSignLine(now: Date, timeZone?: string): string {
  return `The day you sign (${longDateIn(now.toISOString(), timeZone)})`;
}

/** "You’re signing the statement printed above the signature line on page 4." */
export function statementLine(page: number): string {
  return `You’re signing the statement printed above the signature line on page ${page}.`;
}

/**
 * "Signing doesn’t send the form to Maryland." (approved, D-CCM-22 item 3), by jurisdiction, or
 * no line at all where the filing's jurisdiction isn't known here: the approved line names it.
 */
export function doesntSendLine(jurisdiction: string): string | undefined {
  const to = jurisdiction.trim();
  return to.length > 0 ? `Signing doesn’t send the form to ${to}.` : undefined;
}

/** "You signed on November 17, 2026 at 9:12 AM." (frame 40). */
export function youSignedLine(signedAt: string | undefined, timeZone?: string): string {
  return `You signed on ${longDateIn(signedAt, timeZone)} at ${timeIn(signedAt, timeZone)}.`;
}

/**
 * Q7 (approved, D-CCM-29): frame 40's "{sender} has been told." reads "will be told", because the
 * completed notice leaves on the alert sweep's next pass, after the act.
 */
export function willBeToldLine(sender: string | undefined): string {
  return `${sender ?? SOMEONE_IN_YOUR_ORGANIZATION} will be told.`;
}

/** "Maya Solberg will be told, with your reason." (the decline dialog). */
export function toldWithReasonLine(sender: string | undefined): string {
  return `${sender ?? SOMEONE_IN_YOUR_ORGANIZATION} will be told, with your reason.`;
}

/**
 * S11 (approved, D-CCM-29): the signer's own view after declining — frame 42's line, turned to the
 * signer. A decline is not final.
 */
export function youDeclinedLine(
  declinedAt: string | undefined,
  reason: string | undefined,
  timeZone?: string,
): string {
  const on = shortDateIn(declinedAt, timeZone);
  return reason !== undefined && reason.trim().length > 0
    ? `You declined on ${on}: “${reason.trim()}” You can still sign it.`
    : `You declined on ${on}. You can still sign it.`;
}

/** A request closed before the caller signed: frame 43a's and 43b's first sentences. */
export function closedLine(
  detail: SigningRequestDetail,
  personName: PersonName,
): string | undefined {
  const zone = detail.version.timeZone;
  if (detail.request.state === 'cancelled') {
    const by = detail.request.cancelledByUserId;
    const name = by !== undefined ? personName?.(by) : undefined;
    return `Cancelled by ${name ?? SOMEONE_IN_YOUR_ORGANIZATION_MID_SENTENCE} on ${shortDateIn(
      detail.request.cancelledAt,
      zone,
    )}.`;
  }
  if (detail.request.state === 'expired') {
    return `Expired on ${longDateIn(detail.request.expiresAt, zone)}, before it was signed.`;
  }
  return undefined;
}

const COUNT_WORDS = ['One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];

/**
 * "Four more checklist items are marked not needed for this filing." (frame 38), and its one-item
 * form (a mechanical variant, confirmed at the design GO).
 */
export function moreNotNeededLine(count: number): string | undefined {
  if (count <= 0) return undefined;
  const word = COUNT_WORDS[count - 1] ?? String(count);
  return count === 1
    ? `${word} more checklist item is marked not needed for this filing.`
    : `${word} more checklist items are marked not needed for this filing.`;
}

/**
 * S10 (approved, D-CCM-29; D-AS-24/25/26): the organization asks for a sign-in check this account
 * cannot meet here (no way in the prompt accepts, and no passkey), so the page says so before Sign
 * and offers to set up a passkey.
 */
export function cannotMeetCheckLine(organization: string | undefined): string {
  return `${organizationOf(organization)} asks for a sign-in check before signing, and your account can’t meet it here yet. Set up a passkey to sign.`;
}

/**
 * W1 (approved, D-CCM-36; CCM-R1-SIGN-PASSKEY): S10's pattern for Authorize and send. The sign-in
 * check covers sending (D-CCM-27 item 4), so an account that can't meet it here is told so beside
 * the act, with the offer to set up a passkey, never "Please try again".
 */
export function cannotMeetCheckToSendLine(organization: string | undefined): string {
  return `${organizationOf(organization)} asks for a sign-in check before sending, and your account can’t meet it here yet. Set up a passkey to send.`;
}

/**
 * SC1, APPROVED (D-CCM-88, Jonathan 2026-10-14 20:19 PDT, "aprove recs"; Account Security's
 * D-AS-51): S10's and W1's pattern for the sign-in check card's Save (Account Security's
 * AS-PWL-2e). Making the check less strict takes the level in force (contract §7.8), so an account
 * that can't meet it here (an emailed-code account with no passkey) is told so above Save, with the
 * offer to set up a passkey in Save's place, never "please try again".
 */
export function cannotMeetCheckToChangeLine(organization: string | undefined): string {
  return `${organizationOf(organization)} asks for a sign-in check before this setting changes, and your account can’t meet it here yet. Set up a passkey to change it.`;
}

// ── The sign-in check card (frame 82's words; D-CCM-27 item 4) ─────────────────────────────────

export const LEVEL_CARD_TITLE = 'Sign-in check before signing or sending';
// N10 (CCM-R1-SUBMIT-WEB), approved on the cut's renders (D-CCM-33): frame 82's approved line, less
// "sign or": no AI signs (D-CCM-31), and the AI may only send what a person authorized (D-CCM-27).
export const LEVEL_CARD_LINE =
  'Choose whether people must have signed in recently before they sign a filing document, send one, or let their AI send one.';
export const LEVEL_RULE_LINE = 'Making this less strict asks you to pass the current check first.';
// W3 (approved, D-CCM-36; CCM-R1-SIGN-PASSKEY): frame 82's approved line less "Later:" and "in the
// Givmo app". A passkey set up on the web or in the app now confirms "Confirm it's you" (Account
// Security's SEC2-AUTHWEB), wherever passkeys are on (the module's flip waits on that in each
// environment).
export const LEVEL_PASSKEY_LINE =
  'People who set up a passkey confirm with their fingerprint or face.';
export const SAVE = 'Save';

/** The four choices, in D-SIGN-1's order, with the default's own line. */
export const LEVEL_OPTIONS: readonly {
  level: SigningLevel;
  label: string;
  description?: string;
}[] = [
  {
    level: 'signed_in',
    label: 'No extra check',
    description:
      'A signed-in person signs by one deliberate act. Givmo records when they last signed in, and how.',
  },
  { level: 'reauth_within_day', label: 'Signed in within the last day' },
  { level: 'reauth_within_30_minutes', label: 'Signed in within the last 30 minutes' },
  { level: 'reauth_every_act', label: 'Signed in again for every signature' },
];

// ── "Confirm it's you": Compliance's own rows (the agreed seam; `@givmo/step-up` holds none) ────

/**
 * The acts Compliance's pages make that the backend gates on the organization's level, by the
 * refusal's `context.action`: signing's three, and authorizing a filing to be sent
 * (CCM-R1-SUBMIT-WEB; contract §8.2, J2). Approving one's own AI to sign has no page and no row
 * here: no AI signs (D-CCM-31).
 */
export const COMPLIANCE_STEP_UP_ACTIONS = {
  sign: 'sign_compliance_document',
  withdraw: 'withdraw_compliance_signature',
  changeLevel: 'change_compliance_signing_level',
  authorizeSubmission: 'authorize_compliance_submission',
} as const;

/**
 * "{Org} asks everyone to have signed in within the last 30 minutes before {doing}." — the level
 * phrase of frame 39's approved line (and its day and every-signature forms), for one act; or
 * nothing where the level in force isn't known here (its read failed, or it reads as no check):
 * the refusal names the act, never the level, so no level is ever guessed.
 */
function askedToHave(context: StepUpContext, doing: string, everyAct: string): string | undefined {
  const organization = organizationOf(context.organizationName);
  switch (context.level as SigningLevel | undefined) {
    case 'reauth_within_day':
      return `${organization} asks everyone to have signed in within the last day before ${doing}.`;
    case 'reauth_every_act':
      return `${organization} asks everyone to ${everyAct}.`;
    case 'reauth_within_30_minutes':
      return `${organization} asks everyone to have signed in within the last 30 minutes before ${doing}.`;
    default:
      return undefined;
  }
}

/** `@givmo/step-up`'s own line for a prompt it has no row for: it states no level. */
const LEVEL_UNKNOWN_LINE = stepUpWords({}, null).description;

/** A row's line: the level's sentence and what confirming does, or the package's own line. */
function rowLine(asked: string | undefined, then: string): string {
  return asked === undefined ? LEVEL_UNKNOWN_LINE : `${asked} ${then}`;
}

/** "We couldn’t confirm your identity here. Please sign out, sign in again, and {act} from this page." */
function cannotConfirm(act: string): string {
  return `We couldn’t confirm your identity here. Please sign out, sign in again, and ${act} from this page.`;
}

/**
 * The rows the host's gate reads for Compliance's acts. `sign_compliance_document`'s are frame 39's
 * approved words; withdrawing and saving are S2 and S3 with S5's dead ends, approved on the cut's
 * renders (D-CCM-29); authorizing a send is slice 3's frame 64 (D-CCM-27), its day and every-act
 * forms and its dead end on D-CCM-29's pattern (the cut's design go, M1).
 */
export const COMPLIANCE_STEP_UP_COPY: StepUpCopyTable = {
  [COMPLIANCE_STEP_UP_ACTIONS.sign]: {
    description: (context) =>
      rowLine(
        askedToHave(context, 'signing', 'sign in again for every signature'),
        'You’ll sign as soon as you confirm.',
      ),
    confirmLabel: 'Confirm and sign',
    consequence: 'Nothing is signed until you confirm.',
    unavailable: cannotConfirm('sign'),
  },
  // S2 (approved, D-CCM-29).
  [COMPLIANCE_STEP_UP_ACTIONS.withdraw]: {
    description: (context) =>
      rowLine(
        askedToHave(
          context,
          'withdrawing a signature',
          'sign in again before withdrawing a signature',
        ),
        'It’s withdrawn as soon as you confirm.',
      ),
    confirmLabel: 'Confirm and withdraw',
    consequence: 'Nothing is withdrawn until you confirm.',
    unavailable: cannotConfirm('withdraw'),
  },
  // S3 (approved, D-CCM-29).
  [COMPLIANCE_STEP_UP_ACTIONS.changeLevel]: {
    description: (context) =>
      rowLine(
        askedToHave(
          context,
          'making the sign-in check less strict',
          'sign in again before making the sign-in check less strict',
        ),
        'It’s saved as soon as you confirm.',
      ),
    confirmLabel: 'Confirm and save',
    consequence: 'Nothing is saved until you confirm.',
    unavailable: cannotConfirm('save'),
  },
  // Frame 64 (approved, D-CCM-27): "…before sending a filing. It's sent as soon as you confirm."
  [COMPLIANCE_STEP_UP_ACTIONS.authorizeSubmission]: {
    description: (context) =>
      rowLine(
        askedToHave(context, 'sending a filing', 'sign in again before sending a filing'),
        'It’s sent as soon as you confirm.',
      ),
    confirmLabel: 'Confirm and send',
    consequence: 'Nothing is sent until you confirm.',
    unavailable: cannotConfirm('send'),
  },
};

/** The test-id family Compliance's prompts render under. */
export const COMPLIANCE_STEP_UP_TEST_ID_PREFIX = 'compliance-step-up';
